import { ErrorHandler, Injectable } from '@angular/core';
import { ErrorHandlerService, isOffline } from './error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { captureException } from '@sentry/angular';

const CHUNK_LOAD_ERROR = /Loading (CSS )?chunk \S+ failed/;
const CHUNK_RELOAD_MARKER = 'chunk-load-reloads';
const MAX_RELOADS_PER_SESSION = 2;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private errorHandlerService: ErrorHandlerService) {}

  handleError(error) {
    const message = String(error?.message ?? error);

    // A client that fails to fetch a lazy chunk (stale bundle after a deploy,
    // flaky network) recovers by reloading instead of showing a dead page;
    // persistent failures still get reported.
    if (error?.name === 'ChunkLoadError' || CHUNK_LOAD_ERROR.test(message)) {
      if (isOffline()) {
        // Connectivity problem, not a broken bundle — same handling as the
        // http offline strategy.
        this.errorHandlerService.handleOfflineError(message);
        return;
      }
      if (this.attemptRecovery(error)) return;
    }

    if (error instanceof Error) {
      captureException(error);
    }

    if (error instanceof HttpErrorResponse) {
      this.errorHandlerService.handleHttpError(error);
    } else {
      const rejection = error?.rejection;
      if (rejection instanceof HttpErrorResponse) {
        this.errorHandlerService.handleHttpError(rejection);
      } else {
        this.errorHandlerService.handleError(error);
      }
    }
  }

  // Reloads at most twice per tab session, and only after confirming the
  // server is reachable — reloading into a dead network would trade the app
  // shell for a browser error page. Anything else reports as usual.
  private attemptRecovery(error): boolean {
    try {
      const reloads = Number(sessionStorage.getItem(CHUNK_RELOAD_MARKER) ?? 0);
      if (reloads >= MAX_RELOADS_PER_SESSION) return false;
      sessionStorage.setItem(CHUNK_RELOAD_MARKER, String(reloads + 1));

      fetch('/', { method: 'HEAD', cache: 'no-store' })
        .then(() => window.location.reload())
        .catch(() => captureException(error));
      return true;
    } catch {
      return false;
    }
  }
}
